import { StarIcon } from "@heroicons/react/20/solid";
import { ICommunity } from "@sp-crm/core";
import { NgBylinePill } from "components/byline/ng-byline-pill";
import { getMarkerIconColorFromContractStatus } from "components/map/map-common";
import { NextgenEntityMap } from "components/map/nextgen-entity-map";
import { PipelineStageActionBars } from "components/references/show-reference/pipeline-stage-picker";
import { ReferenceTierActionBar } from "components/references/show-reference/reference-tier-picker";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { AutosavingInput } from "components/ui/autosaving-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { Rating } from "components/ui/rating";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React, { useCallback } from "react";
import { Thumbnail } from "./thumbnail";
import { ThumbnailUpload } from "./thumbnail-upload";

interface CommunityActionBarProps {
    community: ICommunity;
    onFieldChange: (fieldName: string, newValue: string | number | null) => void;
    onReloadData: () => void;
}

export const CommunityActionBar: React.FC<CommunityActionBarProps> = ({
    community,
    onFieldChange,
    onReloadData,
}) => {
    const clearRating = useCallback(async (): Promise<void> => {
        const result = await fancyConfirm(
            "Clear rating?",
            "Would you like to clear the rating for this community?",
            "Yes, clear",
            "Cancel",
        );
        if (result) {
            onFieldChange("rating", null);
        }
    }, [onFieldChange]);

    const ratingValue =
        community.rating && community.rating > 0 ? (
            <span className="flex items-center gap-1">
                {community.rating}
                <StarIcon className="h-4 w-4 text-yellow-400" />
            </span>
        ) : (
            "Unrated"
        );

    return (
        <div className="action-bar p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex-grow space-y-3">
                    <div className="flex flex-row flex-wrap items-start justify-center sm:justify-start gap-4 sm:gap-8">
                        <div className="flex-shrink-0">
                            <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                                <div className="w-32 h-32 sm:w-36 sm:h-36 overflow-hidden rounded-lg shadow-sm">
                                    <Thumbnail
                                        community={community}
                                        size="128"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <ThumbnailUpload
                                    communityId={community.id}
                                    reloadCommunity={onReloadData}
                                />
                            </div>
                        </div>

                        <div className="flex basis-full sm:basis-auto sm:flex-1 sm:min-w-0">
                            <div className="flex flex-col w-full space-y-2">
                                <AutosavingInput
                                    multiLine={true}
                                    autoGrow={false}
                                    rows={5}
                                    initial={community.summary}
                                    onCommit={newVal => onFieldChange("summary", newVal)}
                                    placeholder="Summary Notes"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <ActionBarComponent label="Rating" value={ratingValue}>
                            {({ close }) => (
                                <div className="space-y-3">
                                    <Rating
                                        rating={community.rating ?? 0}
                                        onChange={newRating => {
                                            onFieldChange("rating", newRating);
                                            close();
                                        }}
                                    />
                                    {community.rating && community.rating > 0 ? (
                                        <SecondaryCompactButton
                                            onClick={() => {
                                                clearRating();
                                                close();
                                            }}>
                                            Clear rating
                                        </SecondaryCompactButton>
                                    ) : null}
                                </div>
                            )}
                        </ActionBarComponent>
                        <ReferenceTierActionBar
                            sourceType="community"
                            sourceId={community.id}
                        />
                        <PipelineStageActionBars
                            entityType="Community"
                            entityId={community.id}
                        />
                        <NgBylinePill entity={community} mode="action-bar" />
                    </div>
                </div>
                {community.location ? (
                    <div className="hidden w-80 flex-shrink-0 overflow-hidden rounded-lg shadow-sm sm:block lg:w-96">
                        <NextgenEntityMap
                            entityId={community.id}
                            location={community.location}
                            iconType={getMarkerIconColorFromContractStatus(
                                community.contractStatus,
                            )}
                            zoom={11}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
